import { createContext, type ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Anchor, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDataChannel, useIsRecording, useLocalParticipant, useParticipants, useRoomInfo } from '@livekit/components-react';

import { identityUserId, recordingStopperId } from '../../server/participant-identity.ts';
import { participantName } from './call/participant-name';
import { i18n } from '@/i18n';

const RECORDING_TOPIC = 'recording';

export type CloudRecordingStarter = { userId: string; name: string };
export type CloudRecordingInfo = {
    state: 'recording' | 'saving';
    transcribe: boolean;
    startedBy?: CloudRecordingStarter;
    startedAt?: number;
    stoppedAt?: number;
};
export type CloudRecording = {
    isRecording: boolean;
    isSaving: boolean;
    info: CloudRecordingInfo | null;
    seconds: number;
    stop: (roomName: string) => Promise<void>;
};
export type CloudRecordingStopper = { userId: string; name: string; isLocal: boolean; isStarter: boolean };

function parseStarter(value: unknown): CloudRecordingStarter | undefined {
    if (typeof value !== 'object' || value === null) return undefined;
    const starter = value as Partial<Record<keyof CloudRecordingStarter, unknown>>;
    if (typeof starter.userId !== 'string' || starter.userId === '') return undefined;
    return { userId: starter.userId, name: typeof starter.name === 'string' ? starter.name : '' };
}

function positiveTime(value: unknown) {
    return typeof value === 'number' && value > 0 ? value : undefined;
}

function parseCloudRecording(metadata: string | undefined): CloudRecordingInfo | null {
    if (!metadata) return null;
    try {
        const parsed: unknown = JSON.parse(metadata);
        if (typeof parsed !== 'object' || parsed === null || !('cloudRecording' in parsed)) return null;
        const { cloudRecording } = parsed;
        if (typeof cloudRecording !== 'object' || cloudRecording === null) return null;
        const info = cloudRecording as Partial<Record<keyof CloudRecordingInfo, unknown>>;
        return {
            state: info.state === 'saving' ? 'saving' : 'recording',
            transcribe: info.transcribe === true,
            startedBy: parseStarter(info.startedBy),
            startedAt: positiveTime(info.startedAt),
            stoppedAt: positiveTime(info.stoppedAt),
        };
    } catch {
        return null;
    }
}

const CloudRecordingContext = createContext<CloudRecording | null>(null);

const NO_CLOUD_RECORDING: CloudRecording = {
    isRecording: false,
    isSaving: false,
    info: null,
    seconds: 0,
    stop: () => Promise.resolve(),
};

export function useCloudRecording(): CloudRecording {
    return use(CloudRecordingContext) ?? NO_CLOUD_RECORDING;
}

export function CloudRecordingProvider({ children }: Readonly<{ children: ReactNode }>) {
    const roomRecording = useIsRecording();
    const { metadata } = useRoomInfo();
    const isEmployee = useLocalEmployeeId() !== null;
    const info = useMemo(() => parseCloudRecording(metadata), [metadata]);
    const [hadInfo, setHadInfo] = useState(info !== null);
    const [ignoreRoomRecording, setIgnoreRoomRecording] = useState(false);
    const stoppedHereRef = useRef(false);

    if ((info !== null) !== hadInfo) {
        setHadInfo(info !== null);
        if (!info && roomRecording) setIgnoreRoomRecording(true);
    }
    if (ignoreRoomRecording && !roomRecording) setIgnoreRoomRecording(false);

    const isRecording = info ? info.state === 'recording' : roomRecording && !ignoreRoomRecording;
    const isSaving = info?.state === 'saving';
    const runningSeconds = useSecondsSince(isRecording ? info?.startedAt : undefined);
    const savedSeconds =
        info?.startedAt && info.stoppedAt ? Math.max(0, Math.floor((info.stoppedAt - info.startedAt) / 1000)) : 0;

    useDataChannel(RECORDING_TOPIC, (message) => {
        const finished = parseFinished(message.payload);
        if (!finished) return;
        if (roomRecording) setIgnoreRoomRecording(true);
        if (isEmployee) notifyFinished(finished);
    });

    useCloudRecordingNotifications(isRecording, info, stoppedHereRef);

    const stop = useCallback(async (roomName: string) => {
        stoppedHereRef.current = true;
        try {
            await requestCloudRecording(roomName, 'stop');
        } catch (error) {
            stoppedHereRef.current = false;
            throw error;
        }
        notifications.show({
            color: 'success',
            title: i18n.t('conference:recording.notify.stoppedHere.title'),
            message: i18n.t('conference:recording.notify.stoppedHere.message'),
        });
    }, []);

    const value = useMemo(
        () => ({ isRecording, isSaving, info, seconds: isSaving ? savedSeconds : runningSeconds, stop }),
        [isRecording, isSaving, info, savedSeconds, runningSeconds, stop]
    );
    return <CloudRecordingContext value={value}>{children}</CloudRecordingContext>;
}

type FinishedMessage = 'complete' | 'failed';

function parseFinished(payload: Uint8Array): FinishedMessage | null {
    try {
        const message = JSON.parse(new TextDecoder().decode(payload)) as { type?: unknown; status?: unknown };
        if (message.type !== 'finished') return null;
        return message.status === 'complete' ? 'complete' : 'failed';
    } catch {
        return null;
    }
}

function notifyFinished(status: FinishedMessage) {
    if (status === 'failed') {
        notifications.show({
            color: 'danger',
            autoClose: false,
            title: i18n.t('conference:recording.notify.saveFailed.title'),
            message: i18n.t('conference:recording.notify.saveFailed.message'),
        });
        return;
    }
    notifications.show({
        color: 'success',
        title: i18n.t('conference:recording.notify.saved.title'),
        message: (
            <Stack align="flex-start" gap="var(--spacing-1)">
                <Text size="sm">{i18n.t('conference:recording.notify.saved.message')}</Text>
                <Anchor href="/recordings" rel="noopener" size="sm" target="_blank">
                    {i18n.t('conference:recording.notify.saved.link')}
                </Anchor>
            </Stack>
        ),
    });
}

function useCloudRecordingNotifications(
    isRecording: boolean,
    info: CloudRecordingInfo | null,
    stoppedHereRef: { current: boolean }
) {
    const wasRecordingRef = useRef(false);
    const lastInfoRef = useRef<CloudRecordingInfo | null>(null);

    useEffect(() => {
        if (info) lastInfoRef.current = info;
    }, [info]);

    useEffect(() => {
        if (isRecording === wasRecordingRef.current) return;
        wasRecordingRef.current = isRecording;
        const transcribe = lastInfoRef.current?.transcribe ?? false;
        if (isRecording) {
            notifications.show({
                color: 'danger',
                title: i18n.t('conference:recording.notify.cloudStarted.title'),
                message: transcribe
                    ? i18n.t('conference:recording.notify.cloudStarted.transcript')
                    : i18n.t('conference:recording.notify.cloudStarted.plain'),
            });
            return;
        }
        if (stoppedHereRef.current) {
            stoppedHereRef.current = false;
            return;
        }
        notifications.show({
            color: 'success',
            title: i18n.t('conference:recording.notify.cloudStopped.title'),
            message: transcribe
                ? i18n.t('conference:recording.notify.cloudStopped.message')
                : i18n.t('conference:recording.notify.cloudStopped.messagePlain'),
        });
    }, [isRecording, stoppedHereRef]);
}

export function useLocalEmployeeId(): string | null {
    const { localParticipant } = useLocalParticipant();
    return identityUserId(localParticipant.identity);
}

export function useCloudRecordingStopper(info: CloudRecordingInfo | null): CloudRecordingStopper | null {
    const participants = useParticipants();
    const starter = info?.startedBy;
    return useMemo(() => {
        const stopperId = recordingStopperId(
            starter?.userId ?? null,
            participants.map(({ identity, joinedAt }) => ({
                identity,
                joinedAtSeconds: joinedAt ? Math.floor(joinedAt.getTime() / 1000) : Number.MAX_SAFE_INTEGER,
            }))
        );
        if (!stopperId) return null;
        const participant = participants.find(({ identity }) => identityUserId(identity) === stopperId);
        const isStarter = stopperId === starter?.userId;
        const participantDisplayName = participant ? participantName(participant.name, participant.identity) : '';
        return {
            userId: stopperId,
            name: isStarter ? starter.name : participantDisplayName,
            isLocal: participants.some((candidate) => candidate.isLocal && identityUserId(candidate.identity) === stopperId),
            isStarter,
        };
    }, [participants, starter]);
}

export async function requestCloudRecording(roomName: string, action: 'start' | 'stop', transcribe = false) {
    const params = new URLSearchParams({ roomName });
    if (action === 'start') params.set('transcribe', transcribe ? '1' : '0');
    const response = await fetch(`/api/record/${action}?${params.toString()}`);
    if (!response.ok) throw new Error(`${String(response.status)} ${response.statusText}`);
}

export function useSecondsSince(startedAt: number | undefined): number {
    const [now, setNow] = useState(Date.now);

    useEffect(() => {
        if (startedAt === undefined) return;
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [startedAt]);

    return startedAt === undefined ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000));
}
